import React from "react";
//#region types
import { CustomerFormContainerProps } from "models/propTypes/customerPropTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { InvoiceConstants, FormFieldConstants, CommonConstants } from "utils/constants";
import { CustomerFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { CustomerEnums } from "models/enums";
//#endregion
//#region  components
import { useCustomerService } from "serviceHooks";
//#endregion
import * as Yup from "yup";
import { LocalizationHelperInstance, RegularExpressions, UtilsHelper } from "utils/helpers";
import { CustomerFormSections } from "models/enums/customer";

function useCustomerCreateHelpers(props: CustomerFormContainerProps) {
  const customerService = useCustomerService();

  const { customer } = props;

  const customerId = customer?.id;

  const getDefaultValues = (customer?: WintSuperkollAPIModel.Customer): CustomerFormTypes.Create => {
    const areAddressesEqual =
      JSON.stringify(customer?.billingAddress ?? "") === JSON.stringify(customer?.deliveryAddress ?? "");

    return {
      id: customer?.id,
      alreadyExists: false,
      generalInfo: {
        name: customer?.name,
        orgNumber: customer?.orgNumber?.replace("-", ""),
        emails: UtilsHelper.splitMultipleEmails(customer?.emailAddress).map((email) => ({ id: email, value: email })),
        phoneNumber: customer?.phoneNumber,
        referencePersons: customer?.referencePersonsList?.map(
          (p) =>
            ({
              customerId: customerId,
              id: p.id,
              name: p.name,
              phoneNumber: p.phone,
              referenceId: p.referenceIdentifier,
              email: p.emailAddress,
              isDefault: p.isDefault,
            }) as CustomerFormTypes.ReferencePerson
        ),
        billingAddress: {
          ...customer?.billingAddress,
          countryCode: customer?.billingAddress?.countryCode ?? InvoiceConstants.DefaultInvoiceCountry,
        },
        deliveryAddress: {
          ...customer?.deliveryAddress,
          countryCode: customer?.deliveryAddress?.countryCode ?? InvoiceConstants.DefaultInvoiceCountry,
        },
        isSameDeliveryAddress: customer ? areAddressesEqual : true,
        active: !customer?.inactive,
      },
      rutRotSettings: {
        propertyDesignation: customer?.propertyDesignation,
        tenantsOwnerAssociationOrgNumber: customer?.tenantsOwnerAssociationOrgNumber?.replace("-", ""),
        apartmentNumber: customer?.apartmentNumber,
      },
      otherDetailsSettings: {
        includeVATNumber: !customer?.id || (customer?.includeVatNumberOnInvoice ?? false),
        glnNumber: customer?.glnNumber,
        vatNumber: customer?.vatNumber,
      },
      paymentSettings: {
        paymentTerms: customer?.paymentTerms,
        isAutogiro: !!customer?.autogiroPayerNumber,
        autogiroPayerNumber: customer?.autogiroPayerNumber,
      },
      displaySettings: {
        language: customer?.language ?? InvoiceConstants.DefaultInvoiceLanguageCode,
        showCurrencyCode: customer?.showCurrencyCode ?? true,
      },
      sendOptions: {
        settingType: customer?.invoiceDeliverySettings?.enableDeliverySettings
          ? CustomerEnums.CustomerSettingType.CustomerSpecific
          : CustomerEnums.CustomerSettingType.Standard,
        invoiceSendType: customer?.invoiceDeliverySettings?.defaultInvoiceSendOption,
        invoiceTitleTemplate: customer?.invoiceDeliverySettings?.invoiceTitleTemplate,
        invoiceMessageTemplate: customer?.invoiceDeliverySettings?.invoiceMessageTemplate,
        invoiceFileNameTemplate: customer?.invoiceDeliverySettings?.invoiceFileNameTemplate,
        invoiceSendTo: customer?.invoiceDeliverySettings?.invoiceMailToType,
        invoiceSendToEmail: customer?.invoiceDeliverySettings?.customInvoiceMailTo,
        invoiceNotesTemplate: customer?.invoiceDeliverySettings?.invoiceNotes,
      },
      reminderSettings: {
        settingType: customer?.invoiceReminderSettings?.enableReminderSettings
          ? CustomerEnums.CustomerSettingType.CustomerSpecific
          : CustomerEnums.CustomerSettingType.Standard,
        reminderCost: customer?.invoiceReminderSettings?.reminderCost,
        penaltyInterestType: customer?.penaltyInterest?.type,
        penaltyInterestPercentage: customer?.penaltyInterest?.rate ? customer?.penaltyInterest?.rate * 100 : undefined,
        isAutomaticRemindersEnabled:
          customer?.invoiceReminderSettings?.isAutomaticRemindersEnabled === undefined
            ? undefined
            : customer.invoiceReminderSettings.isAutomaticRemindersEnabled
              ? 1
              : 0,
        automaticReminderAmountLimit: customer?.invoiceReminderSettings?.automaticReminderAmountLimit,
        automaticReminderDelay: customer?.invoiceReminderSettings?.automaticReminderDelay,
        automaticReminderPeriod: customer?.invoiceReminderSettings?.automaticReminderPeriod,
        automaticReminderCountLimit: customer?.invoiceReminderSettings?.automaticReminderCountLimit,
        invoiceReminderNotesTemplate: customer?.invoiceReminderSettings?.invoiceReminderNotesTemplate,
        reminderSendType: customer?.invoiceReminderSettings?.invoiceReminderSendOption,
        invoiceReminderTitleTemplate: customer?.invoiceReminderSettings?.invoiceReminderTitleTemplate,
        invoiceReminderMessageTemplate: customer?.invoiceReminderSettings?.invoiceReminderMessageTemplate,
        invoiceReminderFileNameTemplate: customer?.invoiceReminderSettings?.invoiceReminderFilenameTemplate,
        invoiceReminderSendTo: customer?.invoiceReminderSettings?.invoiceReminderMailToType,
        invoiceReminderSendToEmail: customer?.invoiceReminderSettings?.customInvoiceReminderMailTo,
      },
    };
  };

  const [defaultValues, setDefaultValues] = React.useState<CustomerFormTypes.Create>();

  React.useEffect(() => {
    setDefaultValues(getDefaultValues(props.customer));
  }, [props.customer]);

  const addressSchema = Yup.object<CustomerFormTypes.Address>({
    street1: Yup.string().matches(RegularExpressions.Address, {
      message: TranslationKeys.Validations.InvalidCharacters,
      excludeEmptyString: true,
    }),
    street2: Yup.string().matches(RegularExpressions.Address, {
      message: TranslationKeys.Validations.InvalidCharacters,
      excludeEmptyString: true,
    }),
    city: Yup.string()
      .matches(RegularExpressions.Address, {
        message: TranslationKeys.Validations.InvalidCharacters,
        excludeEmptyString: true,
      })
      .required(TranslationKeys.Validations.Required),
    zipCode: Yup.string().matches(RegularExpressions.Address, {
      message: TranslationKeys.Validations.InvalidCharacters,
      excludeEmptyString: true,
    }),
    countryCode: Yup.string().required(TranslationKeys.Validations.Required),
  });

  const getValidationSchema = (section?: CustomerFormSections) => {
    const allowGeneral = section === undefined || section === CustomerFormSections.General;
    const allowInvocing = section === undefined || section === CustomerFormSections.Invoicing;

    const validationSchema = Yup.object<CustomerFormTypes.Create>({
      id: Yup.number(),
      fromRegistry: Yup.mixed(),
      alreadyExists: allowGeneral
        ? Yup.boolean().required().oneOf([false], TranslationKeys.Errors.DuplicateCustomer)
        : Yup.boolean(),
      generalInfo: allowGeneral
        ? Yup.object<CustomerFormTypes.GeneralInfo>({
            name: Yup.string().required(TranslationKeys.Validations.Required),
            orgNumber: Yup.string().matches(RegularExpressions.OrgNr, {
              message: TranslationKeys.Validations.InvalidOrgNumber,
              excludeEmptyString: true,
            }),
            emails: Yup.array()
              .of(
                Yup.object<AutocompleteListItem>({
                  id: Yup.string(),
                  value: Yup.string().email(TranslationKeys.Validations.InvalidEmail),
                })
              )
              .compact((v: AutocompleteListItem) => !v || !v.value)
              .unique(TranslationKeys.Validations.EmailNotUnique, "value"),
            phoneNumber: Yup.string().matches(RegularExpressions.PhoneNumber, {
              message: TranslationKeys.Validations.InvalidPhoneNumber,
              excludeEmptyString: true,
            }),
            referencePersons: Yup.array(),
            billingAddress: addressSchema,
            deliveryAddress: Yup.object<CustomerFormTypes.Address>().when(
              FormFieldConstants.Customer.GeneralInfo.IsSameDeliveryAddress,
              ([isSameDeliveryAddress], schema: Yup.ObjectSchema<{}, CustomerFormTypes.Address>) =>
                isSameDeliveryAddress ? schema : addressSchema
            ),
            isSameDeliveryAddress: Yup.boolean(),
            active: Yup.boolean(),
          })
        : Yup.object(),
      rutRotSettings: allowGeneral
        ? Yup.object<CustomerFormTypes.RutRotSettings>({
            propertyDesignation: Yup.string(),
            tenantsOwnerAssociationOrgNumberFormatted: Yup.string(),
            apartmentNumber: Yup.string(),
          })
        : Yup.object(),
      otherDetailsSettings: allowGeneral
        ? Yup.object<CustomerFormTypes.OtherDetailsSettings>({
            glnNumber: Yup.string(),
            vatNumber: Yup.string(),
            includeVATNumber: Yup.boolean(),
          })
        : Yup.object(),
      paymentSettings: allowInvocing
        ? Yup.object<CustomerFormTypes.PaymentSettings>({
            paymentTerms: Yup.number(),
            isAutogiro: Yup.boolean(),
            autogiroPayerNumber: Yup.string().when(
              FormFieldConstants.Customer.PaymentSettings.IsAutogiro,
              ([isAutogiro], schema: Yup.StringSchema) =>
                (isAutogiro && schema.required(TranslationKeys.Validations.Required)) || schema
            ),
          })
        : Yup.object(),
      displaySettings: allowInvocing
        ? Yup.object<CustomerFormTypes.DisplaySettings>({
            language: Yup.string(),
            showCurrencyCode: Yup.boolean(),
          })
        : Yup.object(),
      sendOptions: allowInvocing
        ? Yup.object<CustomerFormTypes.InvoiceSendOptions>({
            settingType: Yup.number().required(TranslationKeys.Validations.Required),
            invoiceSendType: Yup.number(),
            invoiceSendEmailFrom: Yup.number(),
            invoiceTitleTemplate: Yup.string(),
            invoiceMessageTemplate: Yup.string(),
            invoiceFileNameTemplate: Yup.string(),
            invoiceSendTo: Yup.number(),
            invoiceSendToEmail: Yup.string(),
            invoiceNotesTemplate: Yup.string(),
          })
        : Yup.object(),
      reminderSettings: allowInvocing
        ? Yup.object<CustomerFormTypes.ReminderSettings>({
            settingType: Yup.number().required(TranslationKeys.Validations.Required),
            reminderCost: Yup.number(),
            penaltyInterestType: Yup.number(),
            penaltyInterestPercentage: Yup.number(),
            isAutomaticRemindersEnabled: Yup.number(),
            automaticReminderAmountLimit: Yup.number(),
            automaticReminderDelay: Yup.number(),
            automaticReminderPeriod: Yup.number().when(
              FormFieldConstants.Customer.ReminderSettings.AutomaticReminderDelay,
              ([automaticReminderDelay], schema: Yup.NumberSchema) => {
                const minValue = automaticReminderDelay ? Math.max(5, automaticReminderDelay + 1) : 5;
                return schema.min(
                  minValue,
                  LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Validations.Min, "", {
                    min: `${minValue}`,
                  })
                );
              }
            ),
            automaticReminderCountLimit: Yup.number(),
            invoiceReminderNotesTemplate: Yup.string(),
            reminderSendType: Yup.number(),
            invoiceReminderTitleTemplate: Yup.string(),
            invoiceReminderMessageTemplate: Yup.string(),
            invoiceReminderFileNameTemplate: Yup.string(),
            invoiceReminderSendTo: Yup.number(),
            invoiceReminderSendToEmail: Yup.string(),
          })
        : Yup.object(),
    });

    return validationSchema;
  };

  const handleSubmit = (values: CustomerFormTypes.Create) => {
    const devliverySettingsEnabled =
      values.sendOptions?.settingType === CustomerEnums.CustomerSettingType.CustomerSpecific;
    const reminderSettingsEnabled =
      values.reminderSettings?.settingType === CustomerEnums.CustomerSettingType.CustomerSpecific;

    const customerPut: WintSuperkollAPIModel.CustomerPut = {
      name: values.generalInfo?.name,
      orgNumber:
        values.generalInfo?.orgNumber && !values.generalInfo?.orgNumber.includes("-")
          ? UtilsHelper.applyMask(values.generalInfo?.orgNumber, CommonConstants.OrganizationNumberMask)
          : values.generalInfo?.orgNumber,
      billingAddress: { ...(values.generalInfo?.billingAddress ?? {}) },
      deliveryAddress: values.generalInfo?.isSameDeliveryAddress
        ? { ...(values.generalInfo?.billingAddress ?? {}) }
        : { ...(values.generalInfo?.deliveryAddress ?? {}) },
      emailAddress: values.generalInfo?.emails
        ?.map((email) => email.value?.trim())
        // De-duplicate after trimming: whitespace-only variants pass uniqueness validation but collapse here.
        .filter((email, index, trimmed) => !!email && trimmed.indexOf(email) === index)
        .join(","),
      phoneNumber: values.generalInfo?.phoneNumber,
      referencePersonsList: values.generalInfo?.referencePersons?.map((r: CustomerFormTypes.ReferencePerson) => ({
        id: r.id === undefined || UtilsHelper.isClientGeneratedId(r.id) ? 0 : r.id,
        customerId: r.customerId ?? 0,
        name: r.name,
        emailAddress: r.email,
        phone: r.phoneNumber,
        referenceIdentifier: r.referenceId,
        isDefault: r.isDefault ?? false,
      })),
      inactive: !values.generalInfo?.active,
      vatNumber: values.otherDetailsSettings?.vatNumber,
      glnNumber: values.otherDetailsSettings?.glnNumber,
      includeVatNumberOnInvoice: values.otherDetailsSettings?.includeVATNumber ?? false,
      propertyDesignation: values.rutRotSettings?.propertyDesignation,
      tenantsOwnerAssociationOrgNumber:
        values.rutRotSettings?.tenantsOwnerAssociationOrgNumber &&
        !values.rutRotSettings?.tenantsOwnerAssociationOrgNumber.includes("-")
          ? UtilsHelper.applyMask(
              values.rutRotSettings?.tenantsOwnerAssociationOrgNumber,
              CommonConstants.OrganizationNumberMask
            )
          : values.rutRotSettings?.tenantsOwnerAssociationOrgNumber,
      apartmentNumber: values.rutRotSettings?.apartmentNumber,
      paymentTerms: values.paymentSettings?.paymentTerms,
      autogiroPayerNumber: values.paymentSettings?.isAutogiro ? values.paymentSettings?.autogiroPayerNumber : "",
      showCurrencyCode: values.displaySettings?.showCurrencyCode ?? false,
      language: values.displaySettings?.language,
      penaltyInterest:
        reminderSettingsEnabled && values.reminderSettings?.penaltyInterestType !== undefined
          ? {
              type: values.reminderSettings.penaltyInterestType,
              rate:
                values.reminderSettings.penaltyInterestType ===
                  WintSuperkollAPIModel.InvoicePenaltyInterestType.Prearranged &&
                !UtilsHelper.isEmpty(values.reminderSettings.penaltyInterestPercentage)
                  ? values.reminderSettings.penaltyInterestPercentage! / 100
                  : undefined,
            }
          : undefined,
      invoiceDeliverySettings: devliverySettingsEnabled
        ? {
            enableDeliverySettings: true,
            defaultInvoiceSendOption: UtilsHelper.isEmpty(values.sendOptions?.invoiceSendType)
              ? undefined
              : values.sendOptions?.invoiceSendType,
            invoiceTitleTemplate: UtilsHelper.isEmpty(values.sendOptions?.invoiceTitleTemplate)
              ? undefined
              : values.sendOptions?.invoiceTitleTemplate,
            invoiceMessageTemplate: UtilsHelper.isEmpty(values.sendOptions?.invoiceMessageTemplate)
              ? undefined
              : values.sendOptions?.invoiceMessageTemplate,
            invoiceFileNameTemplate: UtilsHelper.isEmpty(values.sendOptions?.invoiceFileNameTemplate)
              ? undefined
              : values.sendOptions?.invoiceFileNameTemplate,
            invoiceMailToType: UtilsHelper.isEmpty(values.sendOptions?.invoiceSendTo)
              ? undefined
              : values.sendOptions?.invoiceSendTo,
            customInvoiceMailTo:
              UtilsHelper.isEmpty(values.sendOptions?.invoiceSendToEmail) ||
              values.sendOptions?.invoiceSendTo !== WintSuperkollAPIModel.SendInvoiceMailsToEnum.Other
                ? undefined
                : values.sendOptions?.invoiceSendToEmail,
            invoiceNotes: UtilsHelper.isEmpty(values.sendOptions?.invoiceNotesTemplate)
              ? undefined
              : values.sendOptions?.invoiceNotesTemplate,
          }
        : { enableDeliverySettings: false },
      invoiceReminderSettings: reminderSettingsEnabled
        ? {
            enableReminderSettings: true,
            reminderCost: UtilsHelper.isEmpty(values.reminderSettings?.reminderCost)
              ? undefined
              : values.reminderSettings?.reminderCost,
            isAutomaticRemindersEnabled: UtilsHelper.isEmpty(values.reminderSettings?.isAutomaticRemindersEnabled)
              ? undefined
              : values.reminderSettings?.isAutomaticRemindersEnabled === 1,
            automaticReminderAmountLimit: UtilsHelper.isEmpty(values.reminderSettings?.automaticReminderAmountLimit)
              ? undefined
              : values.reminderSettings?.automaticReminderAmountLimit,
            automaticReminderDelay: UtilsHelper.isEmpty(values.reminderSettings?.automaticReminderDelay)
              ? undefined
              : values.reminderSettings?.automaticReminderDelay,
            automaticReminderPeriod: UtilsHelper.isEmpty(values.reminderSettings?.automaticReminderPeriod)
              ? undefined
              : values.reminderSettings?.automaticReminderPeriod,
            automaticReminderCountLimit: UtilsHelper.isEmpty(values.reminderSettings?.automaticReminderCountLimit)
              ? undefined
              : values.reminderSettings?.automaticReminderCountLimit,
            invoiceReminderNotesTemplate: UtilsHelper.isEmpty(values.reminderSettings?.invoiceReminderNotesTemplate)
              ? undefined
              : values.reminderSettings?.invoiceReminderNotesTemplate,
            invoiceReminderSendOption: UtilsHelper.isEmpty(values.reminderSettings?.reminderSendType)
              ? undefined
              : values.reminderSettings?.reminderSendType,
            invoiceReminderTitleTemplate: UtilsHelper.isEmpty(values.reminderSettings?.invoiceReminderTitleTemplate)
              ? undefined
              : values.reminderSettings?.invoiceReminderTitleTemplate,
            invoiceReminderMessageTemplate: UtilsHelper.isEmpty(values.reminderSettings?.invoiceReminderMessageTemplate)
              ? undefined
              : values.reminderSettings?.invoiceReminderMessageTemplate,
            invoiceReminderFilenameTemplate: UtilsHelper.isEmpty(
              values.reminderSettings?.invoiceReminderFileNameTemplate
            )
              ? undefined
              : values.reminderSettings?.invoiceReminderFileNameTemplate,
            invoiceReminderMailToType: UtilsHelper.isEmpty(values.reminderSettings?.invoiceReminderSendTo)
              ? undefined
              : values.reminderSettings?.invoiceReminderSendTo,
            customInvoiceReminderMailTo:
              UtilsHelper.isEmpty(values.reminderSettings?.invoiceReminderSendToEmail) ||
              values.reminderSettings?.invoiceReminderSendTo !== WintSuperkollAPIModel.SendInvoiceMailsToEnum.Other
                ? undefined
                : values.reminderSettings?.invoiceReminderSendToEmail,
          }
        : { enableReminderSettings: false },
      isRutEnabled: customer?.isRutEnabled ?? false,
      isRotEnabled: customer?.isRotEnabled ?? false,
    };

    return (
      customer?.id
        ? customerService.updateCustomer(customer.id, customerPut)
        : customerService.createCustomer(customerPut)
    ).then((res) => {
      setDefaultValues(values);
      return res.data;
    });
  };

  return { handleSubmit, defaultValues, getValidationSchema };
}

export default useCustomerCreateHelpers;
